import { Dispatch, SetStateAction, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { v4 as uuid } from 'uuid';
import * as R from 'ramda';
import { ApplicationContext } from '../../../context/Contexts';
import { AlternativeCode, CarPartQualityTypeCode } from '../../../helpers/Constants';
import { IReferential } from '../../../interfaces/IReferential';
import { Organization, OrganizationOilMappingRequest } from '../../../interfaces/Organization';
import {
  CaseCalculationAlternativePart,
  CaseCalculationPart,
  GetSuppliersAlternativePartsRequestDto,
  TariffPercentResponse
} from '../../../interfaces/Case';
import {
  GetNonStockSuppliersRequestDto,
  GetSuppliersByStatusRequestDto,
  Workflow,
  WorkflowCalculation,
  WorkflowSupplier
} from '../../../interfaces/Workflow';
import { mapAlternativePartToCalculationPart } from '../../../utils/calculationPartUtils';
import {
  InterrogationSupplierDto,
  SaveValidationResult
} from '../types/alternative-calculation.types';
import {
  buildBundlePackagePart,
  clearRecommendationFlags,
  deselectAllVariants,
  getChildrenParts,
  isNonStockDescriptionMissing,
  isValidPartDescription,
  markAlternativeGroup,
  packingErrorQuantity,
  selectedParent,
  revertAlternativeMapping,
  updatePart,
  uuidToNumber
} from '../utils/calculation.utils';

export interface UseCalculationPartsActionsParams<T extends CaseCalculationPart> {
  calculationParts: T[];
  setCalculationParts: Dispatch<SetStateAction<T[]>>;
  additionTariff: TariffPercentResponse;
  discountTariff: TariffPercentResponse;
  workflow: Workflow;
  workflowCalculation: WorkflowCalculation | null;
  ownStockOrganization: Organization | null;
  variantsEnabled: boolean;
  nonStockSuppliers: WorkflowSupplier[];
  setNonStockSuppliers: (suppliers: WorkflowSupplier[]) => void;
  getPartsLocationOrganizationId: () => number | null;
  getInterrogationOrganizationId: () => number | null;
  interrogationSupplier: InterrogationSupplierDto | null;
  reuseGroupedUniqueId: boolean;
}

export const useCalculationPartsActions = <T extends CaseCalculationPart>(
  params: UseCalculationPartsActionsParams<T>
) => {
  const calculationParts = params.calculationParts as CaseCalculationPart[];
  const setCalculationParts = params.setCalculationParts as unknown as Dispatch<
    SetStateAction<CaseCalculationPart[]>
  >;
  const {
    additionTariff,
    discountTariff,
    workflow,
    workflowCalculation,
    ownStockOrganization,
    variantsEnabled,
    nonStockSuppliers,
    setNonStockSuppliers,
    getPartsLocationOrganizationId,
    getInterrogationOrganizationId,
    interrogationSupplier,
    reuseGroupedUniqueId
  } = params;

  const context = useContext(ApplicationContext);
  const caseService = context.caseService;
  const organizationService = context.organizationService;
  const workflowService = context.workflowService;
  const translatorService = context.translatorService;
  const { enqueueSnackbar } = useSnackbar();

  const toggleExpand = (partId: number) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => ({ ...part, isExpanded: !part.isExpanded }))
    );
  };

  const fetchAlternativeParts = (part: CaseCalculationPart) => {
    if (interrogationSupplier) {
      return caseService.InterogateCaseCalculationsAlternativePartsBySupplier({
        partId: part.id,
        partNumber:
          interrogationSupplier.quality?.code === CarPartQualityTypeCode.OE
            ? part.guideNumber
            : part.alternativeGuideNumber ?? '',
        addition: additionTariff.value,
        discount: discountTariff.value,
        workflowId: workflow.id,
        organizationId: getInterrogationOrganizationId(),
        supplierId: interrogationSupplier.supplierId ?? 0,
        qty: part.pieceNr
      } as GetSuppliersAlternativePartsRequestDto);
    }
    return caseService.InterogateCaseCalculationsAlternativeParts({
      partNumber: part.guideNumber,
      partId: part.id,
      addition: additionTariff.value,
      discount: discountTariff.value,
      workflowId: workflow.id,
      organizationId: getInterrogationOrganizationId(),
      organizationOwnerId: workflow.organizationOwnerId,
      stockPartInterogation: workflow.stockPartInterogation,
      qty: part.pieceNr
    } as GetSuppliersAlternativePartsRequestDto);
  };

  const oilMapping = async (part: CaseCalculationPart): Promise<number | null> => {
    if (!workflowCalculation?.oilMapping) return null;
    const mapping = await organizationService.GetOrganizationOilMapping({
      organizationId: getPartsLocationOrganizationId(),
      partCode: part.guideNumber,
      cupiCode: part.repairMethod,
      auxiliarInformation: part.auxiliarInformation
    } as OrganizationOilMappingRequest);
    return mapping && mapping.price ? mapping.price : null;
  };

  const applyOilMapping = async (cp: CaseCalculationPart) => {
    const oilPrice = await oilMapping(cp);
    if (oilPrice != null) {
      setCalculationParts((all) =>
        updatePart(all, cp.id, (p) => ({ ...p, originalPrice: oilPrice }))
      );
    }
  };

  const getNonStockSuppliers = async () => {
    const suppliers = await workflowService.GetNonStockSuppliers({
      workflowId: workflow.id,
      organizationId: getPartsLocationOrganizationId(),
      takeSupplierDetails: false
    } as GetNonStockSuppliersRequestDto);

    setNonStockSuppliers(suppliers);
  };

  const getInactiveWorkflowSuppliers = async () => {
    if (!workflow.inactiveSupplierNotification) {
      return;
    }
    const inactiveSuppliers = await workflowService.GetWorkflowSuppliersByStatus({
      workflowId: workflow.id,
      organizationId: getPartsLocationOrganizationId(),
      isActive: false,
      supplierId: null
    } as GetSuppliersByStatusRequestDto);

    if (inactiveSuppliers.length > 0) {
      enqueueSnackbar(
        (inactiveSuppliers.length === 1
          ? translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_SINGLE',
              'Achizitia de la furnizorul'
            )
          : translatorService.Tranlate(
              'INACTIVE_SUPPLIERS_MSG_FIRST_PART_MULTIPLE',
              'Achizitia de la furnizorii'
            )) +
          ' ' +
          inactiveSuppliers.map((supplier) => supplier.supplierName).join(', ') +
          ' ' +
          (inactiveSuppliers.length === 1
            ? translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_SINGLE',
                'este momentan blocată!'
              )
            : translatorService.Tranlate(
                'INACTIVE_SUPPLIERS_MSG_SECOND_PART_MULTIPLE',
                'sunt momentan blocate!'
              )),
        { variant: 'warning', autoHideDuration: 6000 }
      );
    }
  };

  const packingErrorMessage = (quantity: number): string =>
    translatorService.Tranlate('MIN_PACKING_ERROR_MSG', 'Cantitatea comandata trebuie sa fie ') +
    quantity +
    translatorService.Tranlate('MIN_PACKING_MULTIPLE_ERROR_MSG', ' sau multiplul acesteia!');

  const pushBundleChildrenToPackage = (
    calcPart: CaseCalculationPart,
    children: CaseCalculationAlternativePart[],
    groupedUniqueId: string
  ) => {
    if (!children.length) {
      return;
    }
    let displayPopup = true;
    let alternativePartString = '';
    children.forEach((child: CaseCalculationAlternativePart) => {
      if (child.alternativePartCode === AlternativeCode.TAXAMEDIU && children.length === 1) {
        displayPopup = false;
      }
      alternativePartString += child.name + '(' + child.alternativePrice + ' RON)' + '\n ';
      calcPart.package.push(buildBundlePackagePart(calcPart, child, groupedUniqueId));
    });
    if (displayPopup) {
      enqueueSnackbar(' Componente adaugate la comanda: \n' + alternativePartString, {
        variant: 'warning',
        style: { whiteSpace: 'pre-line' },
        autoHideDuration: 5000
      });
    }
  };

  const applyAlternativePartSelection = (
    id: number,
    originalPart: CaseCalculationPart,
    alternativePart: CaseCalculationAlternativePart,
    resetVariants = false
  ) => {
    const groupedUniqueId =
      reuseGroupedUniqueId && originalPart.groupedUniqueId ? originalPart.groupedUniqueId : uuid();
    const calcPart = {
      ...originalPart,
      ...mapAlternativePartToCalculationPart(
        alternativePart,
        groupedUniqueId,
        alternativePart.excludeFromOrder
      )
    };

    const findAlternativePartGroup = getChildrenParts(
      originalPart.alternativeParts,
      alternativePart
    );
    const idList = findAlternativePartGroup.map((group) => group.id);
    const base =
      variantsEnabled && !resetVariants
        ? clearRecommendationFlags(originalPart.alternativeParts)
        : deselectAllVariants(originalPart.alternativeParts);
    calcPart.alternativeParts = markAlternativeGroup(base, [id, ...idList], {
      isVariantSelected: true,
      isSelectedAlternativePart: true,
      groupedUniqueId
    });
    calcPart.package = [];

    if (!calcPart.pieceNr) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'VALIDATION_QTY_ZERO_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie mai mare decat 0!'
        ),
        { variant: 'warning', autoHideDuration: 5000 }
      );
      calcPart.unitsError = true;
      setCalculationParts((parts) => updatePart(parts, originalPart.id, () => calcPart));
      return;
    }
    calcPart.unitsError = false;

    const packingQty = packingErrorQuantity(calcPart.pieceNr, calcPart.minQty, calcPart.packing);
    if (packingQty != null) {
      enqueueSnackbar(packingErrorMessage(packingQty), {
        variant: 'warning',
        autoHideDuration: 5000
      });
    }

    pushBundleChildrenToPackage(calcPart, findAlternativePartGroup, groupedUniqueId);

    if (!Number.isInteger(calcPart.pieceNr)) {
      enqueueSnackbar(
        translatorService.Tranlate(
          'DECIMAL_ERROR_MSG',
          'Cantitatea comandata trebuie sa fie un numar intreg fara zecimale'
        ),
        { variant: 'warning', autoHideDuration: 5000 }
      );
    }

    calcPart.unitsError =
      !R.isNil(alternativePart.availableQuantity) &&
      alternativePart.availableQuantity < calcPart.pieceNr;

    if (workflowCalculation?.hasAlternativePartDescription) {
      if (
        isNonStockDescriptionMissing(
          workflowCalculation,
          nonStockSuppliers,
          alternativePart.supplierId,
          alternativePart.isInStock,
          calcPart.alternativePartDescription
        )
      ) {
        enqueueSnackbar(
          translatorService.Tranlate(
            'ALTERNATIVE_PART_DESCRIPTION_ERROR_MSG',
            'Va rugam completati descrierea piesei alternative: minim 3 caractere alfanumerice!'
          ),
          { variant: 'warning', autoHideDuration: 5000 }
        );
        calcPart.descriptionError = true;
      } else {
        calcPart.descriptionError = false;
      }
    }

    setCalculationParts((parts) => updatePart(parts, originalPart.id, () => calcPart));
  };

  const handleAlternativePartSelect = (
    id: number,
    originalPartId: number,
    alternativePart: CaseCalculationAlternativePart,
    resetVariants = false
  ) => {
    const originalPart = calculationParts.find((item) => item.id === originalPartId);
    if (originalPart) {
      applyAlternativePartSelection(id, originalPart, alternativePart, resetVariants);
    }
  };

  const interogate = async (part: CaseCalculationPart) => {
    setCalculationParts((parts) =>
      updatePart(parts, part.id, (p) => ({
        ...revertAlternativeMapping(p),
        isLoading: true,
        isExpanded: true,
        hasBeenInterogated: false
      }))
    );

    const alternativeParts = await fetchAlternativeParts(part);
    alternativeParts.forEach((item) => (item.id = uuidToNumber()));
    const firstOwnStockAlternativePart = alternativeParts.find((x) => x.isOwnStock);

    let interrogated: CaseCalculationPart | null = null;
    setCalculationParts((parts) =>
      updatePart(parts, part.id, (p) => {
        interrogated = { ...p, alternativeParts, isLoading: false, hasBeenInterogated: true };
        return interrogated;
      })
    );

    if (!R.isNil(firstOwnStockAlternativePart) && interrogated) {
      applyAlternativePartSelection(
        firstOwnStockAlternativePart.id,
        interrogated,
        firstOwnStockAlternativePart
      );
    }
  };

  const interogateAlternativeParts = async (part: CaseCalculationPart) => {
    getInactiveWorkflowSuppliers();
    getNonStockSuppliers();
    try {
      await applyOilMapping(part);
      await interogate(part);
    } catch (error) {
      enqueueSnackbar(translatorService.Tranlate('ERROR_MSG', 'Eroare'), { variant: 'error' });
    }
  };

  const toggleAlternativePartSelection = (partId: number, alt: CaseCalculationAlternativePart) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => {
        const groupIds = [alt.id, ...getChildrenParts(part.alternativeParts, alt).map((o) => o.id)];
        const currentlySelected = part.alternativeParts.find(
          (a) => a.id === alt.id
        )?.isVariantSelected;
        if (!currentlySelected) {
          return {
            ...part,
            alternativeParts: markAlternativeGroup(part.alternativeParts, groupIds, {
              isVariantSelected: true,
              isSelectedAlternativePart: false,
              groupedUniqueId: uuid()
            })
          };
        }
        const alternativeParts = markAlternativeGroup(part.alternativeParts, groupIds, {
          isVariantSelected: false,
          isSelectedAlternativePart: false,
          groupedUniqueId: null
        });
        const removingRecommended = part.alternativeParts.some(
          (a) => groupIds.indexOf(a.id) !== -1 && a.isSelectedAlternativePart
        );
        return removingRecommended
          ? { ...revertAlternativeMapping(part), alternativeParts }
          : { ...part, alternativeParts };
      })
    );
  };

  const clearRecommendation = (partId: number) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => ({
        ...revertAlternativeMapping(part),
        alternativeParts: clearRecommendationFlags(part.alternativeParts)
      }))
    );
  };

  const unselectPart = (partId: number) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => ({
        ...revertAlternativeMapping(part),
        alternativeParts: deselectAllVariants(part.alternativeParts)
      }))
    );
  };

  const unselectAll = () => {
    setCalculationParts((parts) =>
      parts.map((part) => ({
        ...revertAlternativeMapping(part),
        alternativeParts: deselectAllVariants(part.alternativeParts)
      }))
    );
  };

  const updateGuideNumber = (partId: number, guideNumber: string) => {
    setCalculationParts((parts) => updatePart(parts, partId, (part) => ({ ...part, guideNumber })));
  };

  const updateOriginalPrice = (partId: number, value: number) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => {
        const originalPrice = value;
        let priceError = part.priceError;
        if (workflowCalculation!.serviceOwnStock && ownStockOrganization) {
          priceError = !part.supplierId && originalPrice === 0;
        }
        return { ...part, originalPrice, priceError };
      })
    );
  };

  const updateCarPartCategory = (partId: number, value: IReferential | null) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => ({
        ...part,
        carPartCategory: value,
        carPartCategoryId: value?.id ?? null
      }))
    );
  };

  const updateAlternativePartDescription = (partId: number, value: string) => {
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => {
        const updated = { ...part, alternativePartDescription: value };
        if (
          selectedParent(part) != null &&
          !R.isNil(workflowCalculation) &&
          workflowCalculation.hasAlternativePartDescription
        ) {
          if (!isValidPartDescription(value)) {
            enqueueSnackbar(
              translatorService.Tranlate(
                'ALTERNATIVE_PART_DESCRIPTION_ERROR_MSG',
                'Va rugam completati descrierea piesei alternative: minim 3 caractere alfanumerice!'
              ),
              { variant: 'warning', autoHideDuration: 5000 }
            );
            updated.descriptionError = true;
          } else {
            updated.descriptionError = false;
          }
        }
        return updated;
      })
    );
  };

  const updatePieceNr = (partId: number, pieceNr: number) => {
    const newValue = isNaN(pieceNr) ? 0 : pieceNr;
    setCalculationParts((parts) =>
      updatePart(parts, partId, (part) => {
        let updated = { ...part, pieceNr: newValue };
        if (updated.package && updated.package.length) {
          updated.package = updated.package.map((pkg: CaseCalculationPart) => ({
            ...pkg,
            pieceNr: newValue
          }));
        }

        if (part.alternativeParts.length > 0) {
          updated = {
            ...revertAlternativeMapping(updated),
            pieceNr: newValue,
            hasBeenInterogated: false,
            alternativeParts: [],
            unitsError: false
          };
          enqueueSnackbar(
            translatorService.Tranlate(
              'REINTEROGATE_ALTERNATIVE_PARTS_QTY',
              'Cantitatea a fost modificată, vă rugăm să reinterogați piesa!'
            ),
            { variant: 'warning', autoHideDuration: 6000 }
          );
          return updated;
        }

        if (selectedParent(part) != null) {
          if (!updated.pieceNr) {
            enqueueSnackbar(
              translatorService.Tranlate(
                'VALIDATION_QTY_ZERO_ERROR_MSG',
                'Cantitatea comandata trebuie sa fie mai mare decat 0!'
              ),
              { variant: 'warning', autoHideDuration: 5000 }
            );
            updated.unitsError = true;
            return updated;
          }
          updated.unitsError = false;

          const packingQty = packingErrorQuantity(newValue, updated.minQty, updated.packing);
          if (packingQty != null) {
            enqueueSnackbar(packingErrorMessage(packingQty), {
              variant: 'warning',
              autoHideDuration: 5000
            });
          }
          updated.unitsError =
            !R.isNil(updated.availableQuantity) && updated.availableQuantity < newValue;
        }

        return updated;
      })
    );
  };

  const validateSelectedParts = <TPart extends CaseCalculationPart>(
    parts: TPart[]
  ): SaveValidationResult<TPart> => {
    let errorString = '';
    let decimalError = false;
    let descriptionError = false;
    let zeroPieceNrError = false;

    const validated = parts.map((part) => {
      if (selectedParent(part) == null) {
        return part;
      }
      if (!part.pieceNr) {
        zeroPieceNrError = true;
        return { ...part, unitsError: true };
      }
      const missingDescription = isNonStockDescriptionMissing(
        workflowCalculation,
        nonStockSuppliers,
        part.supplierId,
        part.isInStock,
        part.alternativePartDescription
      );
      if (missingDescription) {
        descriptionError = true;
      }
      const groupPieceNr = parts
        .filter(
          (p) =>
            p.alternativeGuideNumber === part.alternativeGuideNumber &&
            p.alternativePartCode === part.alternativePartCode &&
            p.supplierId === part.supplierId
        )
        .reduce((sum, p) => sum + p.pieceNr, 0);
      const packingQty = packingErrorQuantity(
        part.pieceNr,
        part.minQty,
        part.packing,
        groupPieceNr
      );
      if (packingQty != null) {
        errorString += packingErrorMessage(packingQty) + ' \n';
        return { ...part, unitsError: true, descriptionError: missingDescription };
      }
      if (!Number.isInteger(part.pieceNr)) {
        decimalError = true;
        return { ...part, unitsError: true, descriptionError: missingDescription };
      }
      const overAvailable =
        !R.isNil(part.availableQuantity) && (part.availableQuantity as number) < part.pieceNr;
      return { ...part, unitsError: overAvailable, descriptionError: missingDescription };
    });

    return { validated, errorString, decimalError, descriptionError, zeroPieceNrError };
  };

  return {
    toggleExpand,
    fetchAlternativeParts,
    oilMapping,
    applyOilMapping,
    getNonStockSuppliers,
    getInactiveWorkflowSuppliers,
    packingErrorMessage,
    pushBundleChildrenToPackage,
    applyAlternativePartSelection,
    handleAlternativePartSelect,
    interogate,
    interogateAlternativeParts,
    toggleAlternativePartSelection,
    clearRecommendation,
    unselectPart,
    unselectAll,
    updateGuideNumber,
    updatePieceNr,
    updateOriginalPrice,
    updateCarPartCategory,
    updateAlternativePartDescription,
    validateSelectedParts
  };
};

export default useCalculationPartsActions;
